import { useEffect, useRef, useState } from "react";

import { toast } from "@performyard/py-components";
import { MAX_BULK_LEVEL_ENTRIES } from "@performyard/service-contracts";

import useUnsavedChanges from "../../../hooks/useUnsavedChanges";

// Keyed by whatever the row's stable id is — assignment `_id` on the competency-assignment tables,
// `competency_id` on the role interior table. A key's ABSENCE means "not edited"; a present key
// with value `null` means "edited to cleared" — the two must stay distinguishable, so no-op
// detection runs at the moment of the edit (see setPendingEdit) rather than by filtering the map
// for non-null entries.
export type PendingLevelEdits = Record<string, number | null>;

export type BulkLevelEditSaveResult = {
	successfulIds: string[];
	failedIds: string[];
};

type UseBulkLevelEditParams = {
	// Owns the actual bulk PATCH call — the two surfaces (assignments vs. role competencies) hit
	// different endpoints with different id field names, so the hook only deals in plain ids.
	onSave: (edits: { id: string; value: number | null }[]) => Promise<BulkLevelEditSaveResult>;
	successMessage: string;
	failureMessage: (failedCount: number) => string;
	errorMessage: string;
	// Identity of the entity being edited (roleId / employeeID). A param-only navigation keeps the
	// same route element mounted, so without this a history jump from one role/employee straight to
	// another carries the previous entity's pending ids into the new one's save.
	scopeKey?: string;
};

type UseBulkLevelEditReturn = {
	editMode: boolean;
	pendingEdits: PendingLevelEdits;
	pendingCount: number;
	// At the per-request cap the surfaces stop taking NEW edits (rows already in pendingEdits stay
	// editable, so reverting one re-opens intake) — see the disabled logic at each call site.
	atCap: boolean;
	isSaving: boolean;
	// True while a dirty exit is awaiting the user's call in DiscardChangesModal.
	confirmingExit: boolean;
	enterEditMode: () => void;
	requestExit: () => void;
	confirmExit: () => void;
	dismissExit: () => void;
	endEdit: () => void;
	setPendingEdit: (id: string, value: number | null, currentValue: number | null) => void;
	save: () => Promise<void>;
};

// Shared bulk-edit state for the "Update Current/Expected Levels" inline editors — owned above the
// grid per the SuperRow invariant: a level/description toggle force-recreates every visible cell
// renderer (useSuperRow's refreshCells({force: true})), so a renderer-local pending value would be
// silently dropped on the very next expand/collapse. Callers pass this state + setPendingEdit down
// as props/context; the cell renderer only reads and calls back.
const useBulkLevelEdit = ({
	onSave,
	successMessage,
	failureMessage,
	errorMessage,
	scopeKey,
}: UseBulkLevelEditParams): UseBulkLevelEditReturn => {
	const [editMode, setEditMode] = useState(false);
	const [pendingEdits, setPendingEdits] = useState<PendingLevelEdits>({});
	const [isSaving, setIsSaving] = useState(false);
	const [confirmingExit, setConfirmingExit] = useState(false);

	// Silent reset (no toast): the only way scopeKey changes mid-session is a navigation the
	// route-leave guard already prompted about — the user chose to discard.
	const scopeKeyRef = useRef(scopeKey);
	useEffect(() => {
		if (scopeKeyRef.current === scopeKey) return;
		scopeKeyRef.current = scopeKey;
		setConfirmingExit(false);
		setPendingEdits({});
		setEditMode(false);
	}, [scopeKey]);

	const pendingCount = Object.keys(pendingEdits).length;
	const atCap = pendingCount >= MAX_BULK_LEVEL_ENTRIES;

	const enterEditMode = () => setEditMode(true);

	// Exit via Cancel or the "Editing …" toggle: silent when clean; when dirty, hands the decision to
	// DiscardChangesModal rather than discarding here. Inert mid-save — the callers' exit controls are
	// disabled then too, but an exit here would "discard" edits the in-flight PATCH still commits.
	const requestExit = () => {
		if (isSaving) return;
		if (pendingCount === 0) {
			setEditMode(false);
			return;
		}
		setConfirmingExit(true);
	};

	const confirmExit = () => {
		setConfirmingExit(false);
		setPendingEdits({});
		setEditMode(false);
	};

	const dismissExit = () => setConfirmingExit(false);

	// Ends the session without the discard prompt — for a surface that just became read-only, where
	// there is no longer a Save or Cancel button to reach.
	const endEdit = () => {
		setConfirmingExit(false);
		setPendingEdits({});
		setEditMode(false);
	};

	// Dropping no-op edits here (rather than filtering the map at save time) is what lets a pending
	// edit accumulate correctly across a page/sort/filter change — once a row scrolls out of view
	// there's no "current value" left on screen to re-check it against.
	const setPendingEdit = (id: string, value: number | null, currentValue: number | null) => {
		setPendingEdits((prev) => {
			if (value === currentValue) {
				if (!(id in prev)) return prev;
				const next = { ...prev };
				delete next[id];
				return next;
			}
			return { ...prev, [id]: value };
		});
	};

	const save = async () => {
		const edits = Object.entries(pendingEdits).map(([id, value]) => ({ id, value }));
		if (edits.length === 0) return;
		// Both endpoints reject the whole payload past this cap. Say so instead of spending a request
		// on a guaranteed rejection — the edits survive either way, but the generic failure gives no
		// hint that saving in smaller batches works.
		if (edits.length > MAX_BULK_LEVEL_ENTRIES) {
			toast.error(
				`You can save up to ${MAX_BULK_LEVEL_ENTRIES} changes at once. Save some of these first, then continue.`
			);
			return;
		}
		setIsSaving(true);
		try {
			const { failedIds } = await onSave(edits);
			if (failedIds.length === 0) {
				toast.success(successMessage);
				setPendingEdits({});
				setEditMode(false);
			} else {
				// Keep only the failed items pending (incl. not_authorized — surfaced like any other
				// failure) so the user can retry/adjust without re-entering everything that succeeded.
				toast.error(failureMessage(failedIds.length));
				const failedSet = new Set(failedIds);
				setPendingEdits((prev) => {
					const next: PendingLevelEdits = {};
					Object.entries(prev).forEach(([id, value]) => {
						if (failedSet.has(id)) next[id] = value;
					});
					return next;
				});
			}
		} catch {
			toast.error(errorMessage);
		} finally {
			setIsSaving(false);
		}
	};

	return {
		editMode,
		pendingEdits,
		pendingCount,
		atCap,
		isSaving,
		confirmingExit,
		enterEditMode,
		requestExit,
		confirmExit,
		dismissExit,
		endEdit,
		setPendingEdit,
		save,
	};
};

export default useBulkLevelEdit;

// Wires the codebase's established "unsaved changes" pattern (react-router route-leave confirm +
// a beforeunload listener, both handled inside useUnsavedChanges) to a bulk-edit session's pending
// count. Split out from useBulkLevelEdit itself so the state hook stays renderable without a Router
// in scope (useUnsavedChanges needs one) — callers use both together.
export const useBulkLevelEditNavGuard = (
	pendingCount: number,
	// Surfaces where a search-param change unmounts the editor (the role interior page swaps tabs via
	// ?tab=) must opt in — for them the navigation really does drop the edits.
	{ blockSearchParamChanges = false }: { blockSearchParamChanges?: boolean } = {}
): void => {
	const { setIsUnsafe, setIsSafe } = useUnsavedChanges({
		message: "You have unsaved level changes. Are you sure you want to leave?",
		// Default off: on the assignment tables, paging/sorting/filtering are search-param navigations
		// that keep the editor mounted, so pending edits survive and prompting would warn about a loss
		// that isn't happening. A real route leave still prompts, because that one does drop them.
		blockSearchParamChanges,
	});

	useEffect(() => {
		if (pendingCount > 0) setIsUnsafe();
		else setIsSafe();
	}, [pendingCount, setIsSafe, setIsUnsafe]);
};
